import { useEffect, useState } from 'react';
import {
  Alert,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import { CaptionText } from '../../shared/typography/PrimaryTypography';
import {
  fetchFileEvents,
  toProblemDetail
} from '../../../services/syntiro_honey/data_ingestion.service';
import { AdminFileItem, FileEventItem, ProblemDetail } from '../../../types/syntiro_honey';

const EVENT_COLOR: Record<string, 'default' | 'info' | 'success' | 'error' | 'warning'> = {
  UPLOADED: 'info',
  QUEUED: 'default',
  PROCESSING: 'info',
  PROCESSED: 'success',
  FAILED: 'error',
  DELETED: 'warning',
  RESTORED: 'success',
  CANCELLED: 'warning'
};

/** "cascadedRecords" → "Cascaded records". */
function humaniseKey(key: string): string {
  const spaced = key
    .replace(/([A-Z])/g, ' $1')
    .toLowerCase()
    .trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/**
 * Renders an event's structured detail as reviewer-facing text ("Cascaded
 * records: 65"), not raw JSON — the audit trail is read by admins, not parsers.
 * @param {Record<string, unknown> | null} detail The structured event context.
 * @returns {string} The formatted detail line, or an em dash when empty.
 */
export function describeEventDetail(detail: Record<string, unknown> | null): string {
  if (detail === null || Object.keys(detail).length === 0) {
    return '—';
  }
  return Object.entries(detail)
    .map(
      ([key, value]) =>
        `${humaniseKey(key)}: ${
          typeof value === 'object' && value !== null ? JSON.stringify(value) : String(value)
        }`
    )
    .join(' · ');
}

interface FileAuditDialogProps {
  /** The file whose audit trail to show, or null (dialog closed). */
  file: AdminFileItem | null;
  /** Close handler. */
  onClose: () => void;
}

/**
 * The file's audit trail (Phase 6): every lifecycle event, attributed and
 * timestamped, oldest first, with the structured detail (e.g. how many records a
 * deletion cascaded). Record-level before/after audit lives on each record's
 * History tab; this dialog is the file-level complement.
 * @returns {JSX.Element}
 */
export default function FileAuditDialog({ file, onClose }: FileAuditDialogProps) {
  const [events, setEvents] = useState<Array<FileEventItem> | null>(null);
  const [problem, setProblem] = useState<ProblemDetail | null>(null);

  useEffect(() => {
    if (file === null) {
      return undefined;
    }
    // Stale-response guard: for an AUDIT trail specifically, file A's late
    // response landing under file B's title would misattribute the one artefact
    // that exists to establish who did what.
    let cancelled = false;
    setEvents(null);
    setProblem(null);
    fetchFileEvents(file.id)
      .then((result) => {
        if (!cancelled) {
          setEvents(result);
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setProblem(toProblemDetail(error));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [file]);

  return (
    <Dialog
      open={file !== null}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="file-audit-dialog-title"
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ pr: 1.5 }}>
        <DialogTitle id="file-audit-dialog-title">Audit trail — {file?.filename}</DialogTitle>
        <IconButton onClick={onClose} aria-label="Close">
          <CloseIcon />
        </IconButton>
      </Stack>
      <DialogContent>
        {problem !== null && (
          <Alert severity="error">{problem.detail ?? problem.title ?? 'Loading failed.'}</Alert>
        )}
        {events === null && problem === null && (
          <Stack alignItems="center" sx={{ py: 4 }}>
            <CircularProgress size={24} />
          </Stack>
        )}
        {events !== null && events.length === 0 && (
          <CaptionText>No events have been recorded for this file.</CaptionText>
        )}
        {events !== null && events.length > 0 && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Event</TableCell>
                <TableCell>Actor</TableCell>
                <TableCell>Detail</TableCell>
                <TableCell>When</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {events.map((event) => (
                <TableRow key={`${event.type}-${event.createdAt}`}>
                  <TableCell>
                    <Chip
                      size="small"
                      label={event.type}
                      color={EVENT_COLOR[event.type] ?? 'default'}
                      variant="outlined"
                    />
                  </TableCell>
                  {/* A uid identifies nobody to an admin; the uid stays as the
                      fallback so trails written before the API resolved names
                      remain attributed. */}
                  <TableCell>{event.actorName ?? event.actorId}</TableCell>
                  <TableCell>
                    <CaptionText>{describeEventDetail(event.detail)}</CaptionText>
                  </TableCell>
                  <TableCell>{new Date(event.createdAt).toLocaleString()}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
    </Dialog>
  );
}
